import { useApp } from 'contexts/app';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import feedbackService from 'services/feedback';
import { getUserPreferredFullName, stripHTML } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { studentAccountUsers, currentAssignmentId, currentAssignment, feedback, setFeedback } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const params = useParams();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState({ feedback: null });
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);

    const currentFeedbackItem = feedback?.find(({ id }) => id === params.feedbackId);
    const feedbackItems = feedback
        ?.filter(({ owner_account_id }) => owner_account_id === currentFeedbackItem.owner_account_id)
        ?.sort((a, b) => a.created_on - b.created_on);
    const currentFeedbackItemIndex = feedbackItems?.findIndex(({ id }) => id === currentFeedbackItem.id);

    const students = studentAccountUsers
        ?.filter((accountUser) => {
            return Boolean(
                feedback?.filter(({ owner_account_id }) => owner_account_id === accountUser.owner_account_id)?.length
            );
        })
        ?.sort((a, b) => {
            const aName = getUserPreferredFullName(a).toLowerCase();
            const bName = getUserPreferredFullName(b).toLowerCase();
            if (aName < bName) return -1;
            if (aName > bName) return 1;
            return 0;
        });
    const currentStudentIndex = students?.findIndex(
        ({ owner_account_id }) => owner_account_id === currentFeedbackItem?.owner_account_id
    );
    const studentsWithoutFeedback = studentAccountUsers?.filter((accountUser) => {
        return !Boolean(
            feedback?.filter(({ owner_account_id }) => owner_account_id === accountUser.owner_account_id)?.length
        );
    });

    const getHighlights = () => {
        const highlights =
            currentFeedbackItem?.feedback?.feedback_type === 'rubric'
                ? currentFeedbackItem?.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)
                      ?.negative?.locations || []
                : currentFeedbackItem?.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)
                      ?.locations || [];
        return highlights.map((item) => ({ id: selectedFeedbackId, value: item }));
    };

    const onClickBack = () => {
        history.push(`/assignments/${currentAssignmentId}/feedback`);
    };

    const onChangeStudent = (ownerAccountId) => {
        const targetStudent = students.find(({ owner_account_id }) => owner_account_id === ownerAccountId);
        const targetFeedback = feedback
            .filter((feedbackItem) => feedbackItem.owner_account_id === targetStudent.owner_account_id)
            .sort((a, b) => b.created_on - a.created_on)[0];
        history.push(`/assignments/${currentAssignmentId}/feedback/${targetFeedback.id}`);
    };

    const onClickPrevStudent = () => {
        const targetStudent = students[currentStudentIndex - 1];
        const targetFeedback = feedback
            .filter((feedbackItem) => feedbackItem.owner_account_id === targetStudent.owner_account_id)
            .sort((a, b) => b.created_on - a.created_on)[0];
        history.push(`/assignments/${currentAssignmentId}/feedback/${targetFeedback.id}`);
    };

    const onClickNextStudent = () => {
        const targetStudent = students[currentStudentIndex + 1];
        const targetFeedback = feedback
            .filter((feedbackItem) => feedbackItem.owner_account_id === targetStudent.owner_account_id)
            .sort((a, b) => b.created_on - a.created_on)[0];
        history.push(`/assignments/${currentAssignmentId}/feedback/${targetFeedback.id}`);
    };

    const onClickPrevAttempt = () => {
        const target = feedbackItems[currentFeedbackItemIndex - 1];
        history.push(`/assignments/${currentAssignmentId}/feedback/${target.id}`);
    };

    const onClickNextAttempt = () => {
        const target = feedbackItems[currentFeedbackItemIndex + 1];
        history.push(`/assignments/${currentAssignmentId}/feedback/${target.id}`);
    };

    const onSelect = async ({ key }) => {
        if (key === 'reset') {
            // not implemented yet
        }
        if (key === 'copy') {
            let feedback = '';
            if (typeof form.feedback === 'string') {
                feedback += stripHTML(form.feedback);
            } else if (form.feedback.feedback_type === 'rubric') {
                form.feedback.improvement_suggestions.forEach(({ criterion_id, positive, negative }, index) => {
                    if (index) feedback += '\n\n';
                    const criterion = currentAssignment.rubric.criteria.find(({ id }) => id === criterion_id);
                    feedback += `${criterion.title}\n\n`;
                    feedback += `${t('feedback-section.positive-title', 'What you did well')}\n\n`;
                    feedback +=
                        !positive.active && positive.text
                            ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                            : positive.text === ''
                              ? t(
                                    'feedback-section.no-feedback',
                                    'No feedback provided. This message will not be passed back.'
                                )
                              : stripHTML(positive.text);
                    feedback += `\n\n${t('feedback-section.improvement-title', 'How you can improve')}\n\n`;
                    feedback +=
                        !negative.active && negative.text
                            ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                            : negative.text === ''
                              ? t(
                                    'feedback-section.no-feedback',
                                    'No feedback provided. This message will not be passed back.'
                                )
                              : stripHTML(negative.text);
                });
            } else if (form.feedback.feedback_type === 'system') {
                feedback += stripHTML(form.feedback.commentary);
                form.feedback.improvement_suggestions
                    .filter(({ active }) => active)
                    .forEach(({ text }) => {
                        feedback += '\n\n';
                        feedback += stripHTML(text);
                    });
            }
            await navigator.clipboard.writeText(feedback);
            message.success(t('message.copied-to-clipboard', 'Copied to clipboard.'));
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: t('dialog.delete-all-feedback.title', 'Delete All Feedback?'),
                description: t(
                    'dialog.delete-all-feedback.description',
                    'Are you sure you want to delete all feedback? This can’t be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('controls.delete', 'Delete'),
                cancelButtonText: t('controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => {
                        const updatedForm =
                            currentFeedbackItem.feedback.feedback_type === 'rubric'
                                ? {
                                      ...current,
                                      feedback: {
                                          ...current.feedback,
                                          improvement_suggestions: current.feedback.improvement_suggestions.map(
                                              (item) => ({
                                                  ...item,
                                                  positive: {
                                                      ...item.positive,
                                                      active: false,
                                                  },
                                                  negative: {
                                                      ...item.negative,
                                                      active: false,
                                                  },
                                              })
                                          ),
                                      },
                                  }
                                : {
                                      ...current,
                                      feedback: {
                                          ...current.feedback,
                                          commentary: '',
                                          improvement_suggestions: current.feedback.improvement_suggestions.map(
                                              (item) => ({
                                                  ...item,
                                                  active: false,
                                              })
                                          ),
                                      },
                                  };
                        const updatedFeedback = {
                            ...omit(currentFeedbackItem, ['_id']),
                            modified_on: new Date().getTime(),
                            feedback: updatedForm.feedback,
                        };
                        setFeedback((current) => {
                            return current.map((feedback) => {
                                return feedback.id === updatedFeedback.id ? updatedFeedback : feedback;
                            });
                        });
                        feedbackService.updateFeedback(updatedFeedback.id, updatedFeedback);
                        return updatedForm;
                    });
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const updateFeedback = (updatedForm) => {
        const updatedFeedback = {
            ...omit(currentFeedbackItem, ['_id']),
            modified_on: new Date().getTime(),
            feedback: updatedForm.feedback,
            is_failed: false,
        };
        setFeedback((current) => {
            return current.map((feedback) => {
                return feedback.id === updatedFeedback.id ? updatedFeedback : feedback;
            });
        });
        feedbackService.updateFeedback(updatedFeedback.id, updatedFeedback);
    };

    useEffect(() => {
        if (currentFeedbackItem) {
            setForm({ feedback: currentFeedbackItem.feedback });
            if (!currentFeedbackItem.is_failed && currentFeedbackItem.feedback.feedback_type === 'system') {
                if (currentFeedbackItem.feedback.improvement_suggestions.filter(({ active }) => active).length) {
                    setSelectedFeedbackId(
                        currentFeedbackItem.feedback.improvement_suggestions.filter(({ active }) => active)[0].id
                    );
                }
            }
        }
    }, [currentFeedbackItem]);

    return (
        <PageContext.Provider
            value={{
                form,
                setForm,
                selectedFeedbackId,
                setSelectedFeedbackId,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                currentFeedbackItem,
                feedbackItems,
                currentFeedbackItemIndex,
                students,
                currentStudentIndex,
                studentsWithoutFeedback,
                highlights: getHighlights(),
                onClickBack,
                onChangeStudent,
                onClickPrevStudent,
                onClickNextStudent,
                onClickPrevAttempt,
                onClickNextAttempt,
                onSelect,
                updateFeedback,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
